// src/components/Dashboard/Dashboard.jsx

import React from 'react';
import { Container, Card, Badge, Spinner, Alert, Row, Col } from 'react-bootstrap';
import useWeb3 from './Hooks/useWeb3';
import Web3 from 'web3';
import PancakeSwapEmbed from './swapembede';

import useFontanaData from './Hooks/useFontanaData';
import useLPData from './Hooks/useLPData';

import './styles/Dashboard.css';

const Dashboard = () => {
  console.log('🔴 Dashboard rendered');

  const { web3, isReady, chainId, isConnected } = useWeb3();
  const { hasContract, data } = useFontanaData();
  const { 
    lpContract, 
    contractAddress: lpAddress, 
    isLoading: lpLoading, 
    error: lpError, 
    hasContract: hasLPContract, 
    data: lpData 
  } = useLPData();

  // Helper function to format token amounts
  const formatTokens = (value) => {
    if (!value) return '0';
    const web3Instance = new Web3();
    return web3Instance.utils.fromWei(value.toString(), 'ether');
  };

  return (
    <div className="dashboard">
      <Container fluid className="dashboard-container py-3 py-md-4">
        <h1 className="dashboard-title">Fontana Ecosystem Dashboard</h1>
        
        {/* Main 3-Column Layout */}
        <Row className="mt-3 g-3 g-lg-4">
          
          {/* COLUMN 1: PancakeSwap */}
          <Col xs={12} lg={4}>
            <PancakeSwapEmbed />
          </Col>

          {/* COLUMN 2: LP Data + LP Contract Status Combined */}
          <Col xs={12} lg={4}>
            <Card className="dashboard-card h-100">
              <Card.Body>
                <h6 className="card-title">Liquidity Pool</h6>
                
                {/* LP Contract Status Section */}
                <div className="card-section mb-4">
                  <h6 className="section-subtitle">Contract Status</h6>
                  
                  {lpLoading ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <p className="text-muted mt-2 mb-0">Connecting to LP contract...</p>
                    </div>
                  ) : lpError ? (
                    <Alert variant="danger" className="mb-0">
                      <strong>Error:</strong> {lpError}
                    </Alert>
                  ) : (
                    <>
                      <div className="mt-3">
                        <strong>Contract Instance:</strong>{' '}
                        {hasLPContract ? (
                          <Badge bg="success">✅ Created</Badge>
                        ) : (
                          <Badge bg="danger">❌ Failed</Badge>
                        )}
                      </div>

                      <div className="mt-2">
                        <strong>Contract Object:</strong>{' '}
                        {lpContract ? (
                          <Badge bg="success">✅ Available</Badge>
                        ) : (
                          <Badge bg="danger">❌ Null</Badge>
                        )}
                      </div>

                      <div className="mt-3">
                        <strong>LP Address:</strong>
                        <div>
                          <code className="code-block">{lpAddress}</code>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* LP Pool Data Section */}
                {hasLPContract && lpData && lpData.token0 && (
                  <div className="card-section">
                    <h6 className="section-subtitle">Pool Data (Live)</h6>
                    
                    <div className="mt-3">
                      <strong>Token 0:</strong>
                      <div>
                        <code className="code-block">{lpData.token0}</code>
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <strong>Token 1:</strong>
                      <div>
                        <code className="code-block">{lpData.token1}</code>
                      </div>
                    </div>

                    <div className="mt-3">
                      <strong>Reserve 0:</strong>
                      <div className="text-value">{formatTokens(lpData.reserve0)}</div>
                    </div>
                    
                    <div className="mt-3">
                      <strong>Reserve 1:</strong>
                      <div className="text-value">{formatTokens(lpData.reserve1)}</div>
                    </div>
                    
                    <div className="mt-3">
                      <strong>Total LP Supply:</strong>
                      <div className="text-value">{formatTokens(lpData.totalSupply)}</div>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* COLUMN 3: Fontana Data + Web3 Status Combined */}
          <Col xs={12} lg={4}>
            <Card className="dashboard-card h-100">
              <Card.Body>
                <h6 className="card-title">Fontana Token & Network</h6>

                {/* Web3 Status Section */}
                <div className="card-section mb-4">
                  <h6 className="section-subtitle">Web3 Connection</h6>
                  
                  <div className="mt-3">
                    <strong>Web3 Instance:</strong>{' '}
                    {web3 ? (
                      <Badge bg="success">✅ Connected</Badge>
                    ) : (
                      <Badge bg="danger">❌ Not Connected</Badge>
                    )}
                  </div>

                  <div className="mt-2">
                    <strong>Ready:</strong>{' '}
                    {isReady ? (
                      <Badge bg="success">✅ Yes</Badge>
                    ) : (
                      <Badge bg="warning">⏳ Initializing...</Badge>
                    )}
                  </div>

                  <div className="mt-2">
                    <strong>Chain ID:</strong>{' '}
                    <Badge bg="info">{chainId} (BSC Testnet)</Badge>
                  </div>

                  <div className="mt-2">
                    <strong>Wallet:</strong>{' '}
                    {isConnected ? (
                      <Badge bg="success">✅ Connected</Badge>
                    ) : (
                      <Badge bg="secondary">❌ Disconnected</Badge>
                    )}
                  </div>
                </div>

                {/* Fontana Token Data Section */}
                {hasContract && data && data.name && (
                  <div className="card-section">
                    <h6 className="section-subtitle">Token Data</h6>
                    
                    <div className="mt-3">
                      <strong>Token Name:</strong>
                      <div className="text-value">{data.name}</div>
                    </div>

                    <div className="mt-3">
                      <strong>Symbol:</strong>
                      <div className="text-value">{data.symbol}</div>
                    </div>
                    
                    <div className="mt-3">
                      <strong>Decimals:</strong>
                      <div className="text-value">{data.decimals}</div>
                    </div>

                    <div className="mt-3">
                      <strong>Total Supply:</strong>
                      <div className="text-value">{formatTokens(data.totalSupply)} FNT</div>
                    </div>
                    
                    <div className="mt-3">
                      <strong>Max Supply:</strong>
                      <div className="text-value">{formatTokens(data.maxSupplyLimit)} FNT</div>
                    </div>
                    
                    <div className="mt-3">
                      <strong>Total Burned:</strong>
                      <div className="text-value">{formatTokens(data.totalTokensBurned)} FNT</div>
                    </div>
                    
                    <div className="mt-3">
                      <strong>Trade Volume:</strong>
                      <div className="text-value">{formatTokens(data.totalTradeVolume)} FNT</div>
                    </div>

                    <div className="mt-3">
                      <strong>Contract Owner:</strong>
                      <div>
                        <code className="code-block">{data.owner}</code>
                      </div>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

        </Row>
      </Container>
    </div>
  );
};

export default Dashboard;